import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, AlertCircle } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, useValidatedForm, isBlank, isEmail } from "@/components/app/FormKit";
import { btnPrimary } from "@/components/app/ui";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const validate = useCallback((f) => {
    const e = {};
    if (isBlank(f.email)) e.email = "Email is required.";
    else if (!isEmail(f.email)) e.email = "Enter a valid email address.";
    if (isBlank(f.password)) e.password = "Password is required.";
    return e;
  }, []);
  const form = useValidatedForm({ email: "", password: "" }, validate);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.check()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.values.email.trim(), password: form.values.password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}!`);
      nav(data.user.role === "admin" ? "/admin" : "/vendor");
    } catch (err) {
      const msg = err.response?.status === 401 ? "Incorrect email or password. Please try again." : apiErr(err.response?.data?.detail) || "Login failed";
      setFormError(msg); toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display text-xl font-extrabold text-neutral-900">Sign in</h1>
      <p className="text-slate-500 text-[13px] mt-0.5">Access your vendor or admin dashboard.</p>
      <form onSubmit={submit} noValidate className="mt-5 space-y-3.5">
        {formError && (
          <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[13px] text-red-700"><AlertCircle size={15} className="mt-0.5 shrink-0" />{formError}</div>
        )}
        <Field label="Email" required type="email" t="login-email" placeholder="you@business.com" autoComplete="email" {...form.bind("email")} />
        <Field label="Password" required type="password" t="login-password" placeholder="••••••••" autoComplete="current-password" {...form.bind("password")} />
        <button type="submit" disabled={loading} data-testid="login-submit" className={`${btnPrimary} w-full !h-10`}>
          {loading ? "Signing in…" : "Sign in"} <ArrowRight size={15} />
        </button>
      </form>
      <p className="text-[13px] text-slate-500 mt-5 text-center">
        New vendor? <Link to="/signup" className="text-orange-500 font-semibold hover:underline" data-testid="login-to-signup">Create your store</Link>
      </p>
      <div className="mt-4 text-[12px] text-slate-600 bg-blue-50 border border-blue-100 rounded-lg p-2.5 font-mono">
        Demo vendor: demo@sell24x7.com / demo1234
      </div>
    </AuthShell>
  );
}
